/**
 * Web push notifications via Firebase Cloud Messaging.
 *
 * Concerns split here from config.ts:
 *   * config.ts → SDK init + env reading
 *   * web-push.ts → permission flow, token register/revoke, foreground
 *     message bus for live UI updates (notification bell badge, toast)
 *
 * The browser pipe mirrors the mobile pipe:
 *   1. Wait for the user to be signed in (AuthContext).
 *   2. Ask for the notification permission. The browser remembers Allow /
 *      Block per origin, so this is a no-op after the first grant.
 *   3. Register the service worker at /firebase-messaging-sw.js.
 *   4. getToken({ vapidKey, serviceWorkerRegistration }) → POST to the
 *      existing /notifications/devices endpoint with platform: 'web'.
 *   5. onMessage() — when the tab is FOREGROUND, FCM does NOT show a tray
 *      banner. We surface a Sonner toast AND fire a CustomEvent that the
 *      notification bell listens for to bump its unread counter without
 *      waiting for the 30s poll.
 *
 * Errors throughout are swallowed and logged — push is a best-effort
 * convenience, never block the admin from working.
 */

import {
  getMessaging, getToken, onMessage, deleteToken, isSupported, type Messaging,
} from 'firebase/messaging'
import { toast } from 'sonner'
import { getFirebaseApp, VAPID_KEY, isFirebaseReady } from './config'
import { AdminPushAdmin } from '@/services/admin/admin-core.service'

/** Fired on every foreground FCM message — the NotificationBell adds a
 *  listener and increments its badge so the user sees the count update
 *  without waiting for the next poll tick. */
export const FCM_FOREGROUND_EVENT = 'fcm:foreground-message'

/**
 * Picks the `tag` field for a foreground Notification. Two DIFFERENT
 * pushes must not replace each other in the OS tray. Priority order:
 *
 *   1. `data.id`         — explicit per-notification id from the backend.
 *                          Ideal because the service-worker code uses the
 *                          same value, so a background-then-foreground
 *                          transition still dedupes the SAME event
 *                          correctly.
 *   2. `data.category`   — fallback for older payloads that only carry the
 *                          category. NOT ideal because every "match" push
 *                          then shares the same tag and the new one
 *                          replaces the previous in the tray — the
 *                          original bug this fix addresses.
 *   3. `future-protea-{ts}` — last-resort unique tag so multiple FCM
 *                          events with neither id nor category each get
 *                          their own slot instead of collapsing into one.
 *
 * Exported for unit testing — also used inline by the foreground message
 * handler below.
 */
export function pickNotificationTag(data: Record<string, unknown> | undefined): string {
  // Preserve the original `||` truthy-coalesce semantics — `data.id` /
  // `data.category` from FCM payloads come through as strings on the
  // wire, but the runtime check tolerates whatever the cast lies about
  // at compile time.
  return (
    (data?.id as string) ||
    (data?.category as string) ||
    `future-protea-${Date.now()}`
  )
}

let messagingRef: Messaging | null = null
let unsubscribeForeground: (() => void) | null = null
let lastRegisteredToken: string | null = null
// Daily ticker for `refreshFcmToken`. Module-level so `unregisterWebPush`
// can clear it on logout (otherwise we'd leak an interval + a closure
// every time the admin signs out and back in).
let refreshIntervalId: ReturnType<typeof setInterval> | null = null
// Visibility-change listener handle — mirror of `refreshIntervalId` so
// we can detach it on logout too. Covers the "laptop closed for a week"
// case where the daily interval would have skipped without ever firing.
let visibilityListener: (() => void) | null = null

/**
 * How often to re-check the FCM token while the admin stays signed in.
 * Firebase rotates tokens on its own (typically when the push subscription
 * gets invalidated server-side, when site-data is cleared, or roughly every
 * ~270 days of inactivity). Without a periodic re-check the backend keeps
 * a stale token in `device_tokens` and every push to this admin silently
 * 404s at the FCM gateway — the bug the original review flagged.
 *
 * 24 hours is generous on purpose: real rotations are rare, this just
 * needs to be short enough that a rotated token gets re-registered within
 * a single working day. Cheap — one `getToken()` plus a single POST
 * only when the token actually changed.
 */
const TOKEN_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000

/**
 * Re-fetch the FCM token and push it to the backend ONLY if it has
 * rotated since the last successful registration. Idempotent — safe to
 * call from any of: daily interval, visibility-change, future hooks.
 *
 * Bails out early on the same "is this even possible right now?" guards
 * `registerWebPush` uses, so a not-yet-permitted browser doesn't spam
 * the console on every visibility tick.
 */
async function refreshFcmToken(): Promise<void> {
  if (!isFirebaseReady()) return
  if (typeof window !== 'undefined' && !window.isSecureContext) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  const messaging = await getMessagingRef()
  if (!messaging) return
  try {
    const swReg = await navigator.serviceWorker.ready
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    })
    if (!token || token === lastRegisteredToken) return
    await AdminPushAdmin.registerDevice({
      token,
      platform: 'web',
      device_model: detectDevice(),
      app_version: import.meta.env.VITE_APP_VERSION ?? 'web-admin',
    })
    lastRegisteredToken = token
    if (typeof window !== 'undefined') {
      (window as unknown as { __FCM_TOKEN__?: string }).__FCM_TOKEN__ = token
    }
  } catch (e) {
    // Best-effort — the next tick (daily interval or next visibility
    // event) will try again. Push isn't critical-path.
    console.warn('[web-push] token refresh failed (non-fatal):', e)
  }
}

async function getMessagingRef(): Promise<Messaging | null> {
  if (messagingRef) return messagingRef
  // `isSupported()` checks every prerequisite FCM Web needs in one shot:
  // ServiceWorker, Push API, Notification API, IndexedDB, secure context.
  // On desktop Firefox / older Edge / Safari < 16.4 some of these are
  // missing and calling `getMessaging()` without the check throws an
  // opaque "messaging/unsupported-browser" error that the previous catch
  // swallowed silently — which is exactly why push appeared to do
  // nothing on those desktops.
  try {
    const supported = await isSupported()
    if (!supported) {
      console.warn('[web-push] FCM messaging not supported in this browser')
      return null
    }
  } catch (e) {
    console.warn('[web-push] isSupported() check failed', e)
    return null
  }
  const app = getFirebaseApp()
  if (!app) return null
  try {
    messagingRef = getMessaging(app)
    return messagingRef
  } catch (e) {
    // Common cause: running over HTTP. Service workers + Push require HTTPS
    // (localhost is exempted by browsers). Logged once so the dev knows.
    console.warn('[web-push] getMessaging failed — HTTPS required for push.', e)
    return null
  }
}

/** One-shot boot wiring. Idempotent — safe to call from AuthContext on
 *  every login. The token round-trip is gated on `isFirebaseReady()` so a
 *  missing config doesn't break login. */
export async function registerWebPush(): Promise<void> {
  if (!isFirebaseReady()) return
  // Push API + Service Workers require a secure context (HTTPS or localhost).
  // Loudly bail when we're served over plain HTTP — `getToken()` would just
  // reject with an opaque error a few lines down otherwise.
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    console.warn('[web-push] Skipping registration — page is not a secure context (HTTPS / localhost required).')
    return
  }
  if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') {
    console.warn('[web-push] Browser does not support Service Workers or Notifications.')
    return
  }
  const messaging = await getMessagingRef()
  if (!messaging) return

  try {
    // Modern Chrome / Edge / Firefox return 'default' the first call and
    // 'granted' / 'denied' thereafter. Safari fires its own prompt.
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return

    // Register the SW AND wait for it to reach the `active` state before
    // handing it to `getToken()`. `register()` resolves the moment the
    // registration object exists — the SW itself may still be `installing`
    // at that point, and `getToken()` then fails to subscribe the push
    // manager. Using the registration returned by `serviceWorker.ready`
    // (resolved only once an active worker is controlling the scope) was
    // the missing piece on desktop where the cold-start race is most
    // common.
    await navigator.serviceWorker.register('/firebase-messaging-sw.js')
    const swReg = await navigator.serviceWorker.ready
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    })
    if (!token) {
      console.warn('[web-push] getToken returned empty — VAPID key or SW scope mismatch?')
      return
    }

    if (token !== lastRegisteredToken) {
      // Reuses the same endpoint mobile clients hit; platform='web' tells
      // the dispatcher to send via FCM Web Push protocol.
      await AdminPushAdmin.registerDevice({
        token,
        platform: 'web',
        device_model: detectDevice(),
        app_version: import.meta.env.VITE_APP_VERSION ?? 'web-admin',
      })
      lastRegisteredToken = token
    }
    // Debug aid: expose the live FCM token on `window` so an operator can
    // copy it from the browser console (`window.__FCM_TOKEN__`) when
    // sending a manual test push through Firebase Console. Cheap, opt-in
    // by name — no risk of clobbering anything app code reads.
    if (typeof window !== 'undefined') {
      (window as unknown as { __FCM_TOKEN__?: string }).__FCM_TOKEN__ = token
      console.info('[web-push] FCM token ready — copy via window.__FCM_TOKEN__')
    }

    // Arm (or re-arm) the token-refresh ticker AND a visibility-change
    // refresh. The interval covers normal long-running sessions; the
    // visibility hook covers the "laptop closed for a week" case where
    // setInterval can sleep through several days of rotation. Both are
    // cleared on logout via `unregisterWebPush`.
    if (refreshIntervalId) clearInterval(refreshIntervalId)
    refreshIntervalId = setInterval(() => { void refreshFcmToken() }, TOKEN_REFRESH_INTERVAL_MS)
    if (visibilityListener) document.removeEventListener('visibilitychange', visibilityListener)
    visibilityListener = () => {
      if (document.visibilityState === 'visible') void refreshFcmToken()
    }
    document.addEventListener('visibilitychange', visibilityListener)

    // Install (or re-install) the foreground listener. Re-running is safe
    // because we tear the old one down first.
    unsubscribeForeground?.()
    unsubscribeForeground = onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? 'Future Protea'
      const body  = payload.notification?.body  ?? ''
      const data  = payload.data ?? {}

      // ── In-tab UX: Sonner toast ───────────────────────────────────
      toast(title, { description: body })

      // ── OS-level pop: real Notification API ───────────────────────
      // FCM doesn't auto-show a tray banner when the tab is focused —
      // that's the browser's design (assumes the page will handle it).
      // We bridge that gap by firing a real OS notification ourselves
      // so the admin gets the same buzz/banner regardless of whether
      // the tab is foregrounded. Falls back gracefully if Notification
      // is unavailable (older browsers / blocked permission).
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, {
            // Per-event tag — see `pickNotificationTag` above for the
            // priority chain and the rationale behind it.
            tag: pickNotificationTag(data),
            body,
            // OPTIONAL icon — the file may not exist; browsers fall back
            // to the favicon when src 404s.
            icon: '/icon-192.png',
            data,
          })
          // Clicking the OS pop focuses our tab and routes to the link
          // the backend put on the FCM payload.
          n.onclick = () => {
            window.focus()
            const link = typeof data.link === 'string' ? data.link : null
            if (link && window.location.pathname !== link) {
              window.history.pushState({}, '', link)
              window.dispatchEvent(new PopStateEvent('popstate'))
            }
            n.close()
          }
        } catch (e) {
          // Don't propagate — the toast already covered the in-tab UX.
          console.warn('[web-push] foreground Notification() failed:', e)
        }
      }

      // ── Bell badge: increment via CustomEvent ─────────────────────
      // The bell component listens for this and bumps its unread count
      // immediately, so the user sees the badge tick without waiting
      // for the next 30s poll.
      window.dispatchEvent(new CustomEvent(FCM_FOREGROUND_EVENT, {
        detail: { title, body, data },
      }))
    })

    // Notification-click messages forwarded by the SW (focus path). We
    // route the admin to the link the backend put on the FCM payload —
    // typically `/matches/:id` or `/users/:id`.
    navigator.serviceWorker.addEventListener('message', (event) => {
      const msg = event.data
      if (msg?.source === 'fcm-click' && typeof msg.link === 'string') {
        // Soft nav — react-router picks this up via popstate.
        if (window.location.pathname !== msg.link) {
          window.history.pushState({}, '', msg.link)
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      }
    })
  } catch (e) {
    console.warn('[web-push] registerWebPush failed (non-fatal):', e)
  }
}

/** Logout cleanup — revoke server-side, drop the local subscription so
 *  the next account's tokens don't share this admin's tray pipe. */
export async function unregisterWebPush(): Promise<void> {
  const token = lastRegisteredToken
  unsubscribeForeground?.()
  unsubscribeForeground = null
  // Tear down the daily refresh ticker + visibility listener installed
  // by `registerWebPush`. Without this the previous admin's interval
  // would keep firing under the next admin's session, re-registering a
  // stale token against the new user.
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener)
    visibilityListener = null
  }
  if (token) {
    try {
      await AdminPushAdmin.revokeDevice(token)
    } catch (e) {
      console.warn('[web-push] revokeDevice failed (best-effort):', e)
    }
  }
  const messaging = await getMessagingRef()
  if (messaging) {
    try { await deleteToken(messaging) } catch { /* swallow */ }
  }
  lastRegisteredToken = null
}

/** Compact device identifier for the device_tokens.device_model column —
 *  picks the most useful piece of UA string we can read without UA-CH
 *  permissions. Falls back to "web-admin" so the column is never blank. */
function detectDevice(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua))     return 'Edge / ' + navigator.platform
  if (/Chrome\//.test(ua))  return 'Chrome / ' + navigator.platform
  if (/Firefox\//.test(ua)) return 'Firefox / ' + navigator.platform
  if (/Safari\//.test(ua))  return 'Safari / ' + navigator.platform
  return 'web-admin'
}
